<script setup lang="ts">
  defineOptions({ name: 'GridLoadingIndicator' })

  import { createElementId } from '@common/core-layer/utils/index'

  import CPText from '@ui/components/CPText.vue'

  defineProps({
    /** First page still loading (infinite query: isLoading/isPending) — show same dots as pagination tail */
    isInitialLoading: {
      type: Boolean,
      default: false,
    },
    isFetchingNextPage: {
      type: Boolean,
      required: true,
    },
    hasNextPage: {
      type: Boolean,
      required: true,
    },
    itemCount: {
      type: Number,
      required: true,
    },
    endOfListMessage: {
      type: String,
      default: '',
    },
    /** Only show end message when itemCount >= this (avoids "All loaded" when first page has fewer items) */
    minItemsForEndMessage: {
      type: Number,
      default: 0,
    },
    dotCount: {
      type: Number,
      default: 3,
    },
  })
</script>

<template>
  <!-- Loading dots: initial empty grid or next page (root only when actually loading to avoid empty spacer) -->
  <div
    v-if="(isInitialLoading && itemCount === 0) || (hasNextPage && isFetchingNextPage)"
    :id="createElementId('grid-loading', 'indicator', 'root')"
    class="flex items-center justify-center py-6"
    aria-hidden="true"
  >
    <div :id="createElementId('grid-loading', 'indicator', 'dots')" class="loading-dots flex items-center gap-1.5">
      <span
        v-for="i in dotCount"
        :id="createElementId('grid-loading', 'indicator', `dot-${i}`)"
        :key="i"
        class="dot"
        :style="{ animationDelay: `${(i - 1) * 150}ms` }"
      />
    </div>
  </div>

  <!-- End-of-list indicator when all data has been loaded (only when no more pages, message provided, and at least one load-more has occurred) -->
  <div
    v-else-if="!hasNextPage && itemCount > 0 && endOfListMessage && (minItemsForEndMessage <= 0 || itemCount >= minItemsForEndMessage)"
    :id="createElementId('grid-loading', 'end', 'container')"
    class="flex items-center justify-center py-4"
  >
    <div :id="createElementId('grid-loading', 'end', 'message')" class="flex items-center gap-2">
      <CPText size="xs" class="text-muted">
        {{ endOfListMessage }}
      </CPText>
    </div>
  </div>
</template>

<style scoped>
  .loading-dots .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--cp-font-400);
    animation: dot-bounce 1.2s infinite ease-in-out;
  }

  @keyframes dot-bounce {
    0%,
    80%,
    100% {
      transform: scale(0.6);
      opacity: 0.4;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
